import { useContext } from "react";
import { createPortal } from "react-dom";
import { Z } from "../lib/tokens"; // 204 §B.2 — the Z scale
import { PaneOpenContext, paneRungFor, useAbovePane } from "../lib/paneStacking"; // 206E §1.3 — over an open pane

export const SideFlyPanel = ({
  sideFly,
  setSideFly,
  t,
  themeCtl,
  personsGrps,
  navI,
  nav,
}: {
  sideFly: any;
  setSideFly: any;
  t: any;
  themeCtl: any;
  /* typed precisely so the body's pg.keys.map((k) => ...) callbacks keep
     the contextual types they had inside App (avoids TS7006 with zero body
     edits) */
  personsGrps: Array<{ id: string; label: any; keys: string[] }>;
  navI: any[];
  nav: any;
}) => {
  /* 206E §1.3 [RULED — Laurent: "… saves one click"] — THE FLYOUT SHOWS ABOVE
     AN OPEN PANE. My Bento and My Eisenhower now stand beside the sidebar, so
     « Persons » and « Theme » can be clicked while one is open — and this
     overlay sat at `Z.sideFly` (190), UNDER the pane's 9999: measured, what
     was painted at the middle of the flyout was the pane. While a pane is
     open it takes the rung a modal shell takes there (`paneRungFor`, 204 §1 —
     `Z.paneModal`), and registers as above the pane for as long as it stands,
     so its Escape (the hotkey chain's `sideFly` arm) closes IT and not the
     pane behind it — measured before: one Escape closed both. With no pane
     open both are inert and the figure is 190, as it was. */
  const paneOpen = useContext(PaneOpenContext);
  useAbovePane(paneOpen && !!sideFly);
  return (
  sideFly &&
  createPortal(
            /* 188 §A.3 — NOT A MODAL, LEFT DELIBERATELY. `modal-overlay` is
               borrowed for its backdrop and its z-index only; what it frames is
               `.PopPanel`, not a `.modal` box, and this is the one shell the
               census found riding a createPortal. ModalLayout (the modal shell,
               S3b) does not apply here; a later pass reading the class must not
               convert this. */
            <div
              className="modal-overlay"
              style={{ zIndex: paneOpen ? paneRungFor(null) : Z.sideFly }}
              onClick={(e) => {
                if (e.target === e.currentTarget) setSideFly(null);
              }}
            >
              <div className="PopPanel">
                <div className="PopPanelH">
                  {sideFly.id === "theme"
                    ? t("sidebar.theme.label", { fallback: "Theme" })
                    : t("nav.persons")}
                </div>
                {sideFly.id === "theme" ? (
                  <div style={{ padding: "4px 14px 14px" }}>
                    {themeCtl.pushNote}
                    {themeCtl.personalNote}
                    <div style={{ display: "flex", gap: 2 }}>
                      {themeCtl.modeButtons}
                    </div>
                  </div>
                ) : (
                  /* Single scrollable column (C7 kept): the four category
                     groups stacked, headers kept. */
                  <div
                    style={{
                      padding: "0 0 8px",
                      overflowY: "auto",
                      minHeight: 0,
                    }}
                  >
                    {personsGrps.map((pg) => {
                      const pgItems = pg.keys
                        .map((k) => navI.find((x) => x.k === k))
                        .filter(Boolean);
                      return (
                        <div key={pg.id}>
                          <div className="sidebar-flyout-header">{pg.label}</div>
                          {pgItems.map((n: any) => (
                            <div
                              key={n.k}
                              /* Phase 41 (2c) — no sec-driven .on here: the
                                 count-pill is hover-only (.PopPanel .NI:hover .B)
                                 and a click navigates straight to the subsection,
                                 so there is no persistent "selected leaf" pill. */
                              className="nav-item"
                              onClick={() => {
                                nav(n.k);
                                setSideFly(null);
                              }}
                            >
                              <span>
                                {n.i && (
                                  <n.i
                                    size={13}
                                    style={{
                                      display: "inline",
                                      verticalAlign: "middle",
                                      marginRight: 4,
                                    }}
                                  />
                                )}
                                <span className="nav-label">{n.l}</span>
                              </span>
                              {/* 210 §1 — the count App drew for the row: its
                                  number, or the words when its list could not
                                  load (App's `navI`, ListNotLoaded's badge). */}
                              {n.cn}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>,
    document.body,
  )
  );
};
